import { lazy, Suspense, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import './index.css'
import '@/lib/i18n'
import { QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { Toaster } from '@/components/ui/sonner'
import { VersionUpdateChecker } from '@/components/shared/VersionUpdateChecker'
import { env } from '@/config/env'
import { DateProvider } from '@/contexts/date-context'
import { queryClient } from '@/lib/react-query'
import { initializeTabOrganization } from '@/lib/tab-organization'

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then(m => ({ default: m.ReactQueryDevtools }))
)

const AppTree = (
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <DateProvider>
          <App />
          <Toaster />
          <VersionUpdateChecker />
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        </DateProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
)

initializeTabOrganization()

const root = createRoot(document.getElementById('root') ?? document.body)

if (env.isDevelopment) {
  root.render(AppTree)
} else {
  // Production: wrap with PostHog for analytics
  import('posthog-js/react').then(({ PostHogProvider }) => {
    root.render(
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
        options={{
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
          autocapture: false,
          capture_pageview: false,
          enable_heatmaps: false,
          opt_out_capturing_by_default: false,
          disable_session_recording: !env.analytics.sessionRecordingEnabled,
          mask_all_text: false,
          session_recording: { maskAllInputs: false },
        }}
      >
        {AppTree}
      </PostHogProvider>
    )
  })
}
